/*!
 * <bold-sc-adapter> — Bold provider (plain JS, no deps)
 * Detects Bold Options (window.BOLD) and PRE (window.BOLD.pre).
 * Reflects status via attributes:
 *   state="waiting|ready|degraded" options="ready|waiting|absent" pre="ready|waiting|absent"
 * Emits:
 *   'hub:adapter:state'  detail: { state, status }
 *   'hub:options:ready'  detail: { bold }
 *   'hub:pre:ready'      detail: { pre }
 * Exposes: el.getStatus(), el.whenReady(cb), el.whenOptionsReady(cb), el.whenPreReady(cb)
 */

(function () {
  var GLOBAL_TIMEOUT_MS = 6000;
  var FORM_TIMEOUT_MS = 4000;
  var PRODUCT_FORM_SELECTOR = '[data-product-form], product-form';

  function waitForGlobal(path, opts) {
    opts = opts || {};
    var timeoutMs = opts.timeoutMs == null ? GLOBAL_TIMEOUT_MS : opts.timeoutMs;
    var startDelayMs = opts.startDelayMs == null ? 50 : opts.startDelayMs;
    var maxDelayMs = opts.maxDelayMs == null ? 800 : opts.maxDelayMs;

    var parts = path.split('.');
    function get() {
      var cur = window;
      for (var i = 0; i < parts.length; i++) {
        if (cur == null) return undefined;
        cur = cur[parts[i]];
      }
      return cur;
    }

    return new Promise(function (resolve) {
      var start = Date.now();
      var delay = startDelayMs;
      (function tick() {
        var val = get();
        if (val != null) return resolve(val);
        if (Date.now() - start >= timeoutMs) return resolve(null);
        setTimeout(tick, delay);
        delay = Math.min(maxDelayMs, delay * 2);
      })();
    });
  }

  function waitForElement(selector, opts) {
    opts = opts || {};
    var root = opts.root || document;
    var timeoutMs = opts.timeoutMs == null ? FORM_TIMEOUT_MS : opts.timeoutMs;

    return new Promise(function (resolve) {
      var done = false;
      var timeoutId = null;

      function resolveOnce(el) {
        if (done) return;
        done = true;
        try { obs && obs.disconnect && obs.disconnect(); } catch (e) {}
        if (timeoutId) clearTimeout(timeoutId);
        resolve(el);
      }

      var first = root.querySelector(selector);
      if (first) return resolveOnce(first);

      var obs = new MutationObserver(function () {
        queueMicrotask(function () {
          if (done) return;
          var el = root.querySelector(selector);
          if (el) resolveOnce(el);
        });
      });

      var target = (root.nodeType === 9) ? root.documentElement : root;
      obs.observe(target, { childList: true, subtree: true });

      if (timeoutMs > 0) {
        timeoutId = setTimeout(function () { resolveOnce(null); }, timeoutMs);
      }
    });
  }

  function capabilityProbePRE(pre) {
    try {
      if (!pre) return false;
      if (typeof pre.ready === 'function') return true;
      var p = pre.process || {};
      return typeof p.product === 'function' || typeof p.cart === 'function';
    } catch (e) { return false; }
  }

  var TAG = 'bold-sc-adapter';
  if (customElements.get(TAG)) return;

  class BoldScAdapter extends HTMLElement {
    constructor() {
      super();
      this._state = 'waiting';
      this._status = { options: 'waiting', pre: 'waiting', note: undefined };
      this._readyResolvers = [];
      this._optionsResolvers = [];
      this._preResolvers = [];
      this._formMarked = false;
      this._booted = false;
      this._didOptionsReadyEvent = false;
      this._didPreReadyEvent = false;
    }

    connectedCallback() {
      if (!this._formMarked) {
        this._formMarked = true;
        waitForElement(PRODUCT_FORM_SELECTOR, { root: document, timeoutMs: FORM_TIMEOUT_MS })
          .then((el) => { if (el) try { el.setAttribute('data-hub-form', '1'); } catch(_){} });
      }
      if (!this._booted) {
        this._booted = true;
        this._detect();
      }
    }

    getStatus() { return { state: this._state, status: this._status }; }

    whenReady(cb) {
      if (typeof cb !== 'function') return;
      if (this._state === 'ready') { queueMicrotask(() => { try { cb(this.getStatus()); } catch(_){} }); return; }
      this._readyResolvers.push(cb);
    }
    whenOptionsReady(cb) {
      if (typeof cb !== 'function') return;
      if (this._status.options === 'ready') { queueMicrotask(() => { try { cb({ bold: window.BOLD }); } catch(_){} }); return; }
      this._optionsResolvers.push(cb);
    }
    whenPreReady(cb) {
      if (typeof cb !== 'function') return;
      if (this._status.pre === 'ready') { queueMicrotask(() => { try { cb({ pre: window.BOLD && window.BOLD.pre }); } catch(_){} }); return; }
      this._preResolvers.push(cb);
    }

    _emit() {
      this.setAttribute('state', this._state);
      this.setAttribute('options', this._status.options);
      this.setAttribute('pre', this._status.pre);
      if (this._status.note) this.setAttribute('note', this._status.note); else this.removeAttribute('note');

      try {
        this.dispatchEvent(new CustomEvent('hub:adapter:state', {
          detail: { state: this._state, status: this._status },
          bubbles: true, composed: true
        }));
      } catch (_) {}

      if (this._state === 'ready' && this._readyResolvers.length) {
        var cbs = this._readyResolvers.slice(); this._readyResolvers.length = 0;
        for (var i = 0; i < cbs.length; i++) { try { cbs[i](this.getStatus()); } catch(_){} }
      }
      if (!this._didOptionsReadyEvent && this._status.options === 'ready') {
        this._didOptionsReadyEvent = true;
        try {
          this.dispatchEvent(new CustomEvent('hub:options:ready', {
            detail: { bold: window.BOLD }, bubbles: true, composed: true
          }));
        } catch (_) {}
        if (this._optionsResolvers.length) {
          var o = this._optionsResolvers.slice(); this._optionsResolvers.length = 0;
          for (var j = 0; j < o.length; j++) { try { o[j]({ bold: window.BOLD }); } catch(_){} }
        }
      }
      if (!this._didPreReadyEvent && this._status.pre === 'ready') {
        this._didPreReadyEvent = true;
        try {
          this.dispatchEvent(new CustomEvent('hub:pre:ready', {
            detail: { pre: window.BOLD && window.BOLD.pre }, bubbles: true, composed: true
          }));
        } catch (_) {}
        if (this._preResolvers.length) {
          var p = this._preResolvers.slice(); this._preResolvers.length = 0;
          for (var k = 0; k < p.length; k++) { try { p[k]({ pre: window.BOLD && window.BOLD.pre }); } catch(_){} }
        }
      }
    }

    _finalize() {
      if (this._status.options === 'ready' || this._status.pre === 'ready') {
        this._state = 'ready';
        this._status.note = undefined;
      } else if (this._status.options === 'absent' && this._status.pre === 'absent') {
        this._state = 'degraded';
        this._status.note = 'No Bold runtimes detected within timeout';
      } else {
        this._state = 'waiting';
        this._status.note = undefined;
      }
      this._emit();
      try { console.info('[bold-sc-adapter]', this._state, this._status); } catch(_) {}
    }

    _detect() {
      var self = this;
      Promise.all([
        waitForGlobal('BOLD', { timeoutMs: GLOBAL_TIMEOUT_MS }),
        waitForGlobal('BOLD.pre', { timeoutMs: GLOBAL_TIMEOUT_MS })
      ]).then(function (vals) {
        var bold = vals[0];
        var pre  = vals[1];

        // Options runtime
        var hasHelpers = !!(bold && bold.helpers);
        var isDomLoaded = !!(bold && bold.options && bold.options.app && bold.options.app.isDomLoaded);
        var hasOptions = hasHelpers || isDomLoaded;
        self._status.options = hasOptions ? 'ready' : (bold ? 'waiting' : 'absent');

        // PRE runtime
        var preLooksReady = capabilityProbePRE(pre);
        if (pre && typeof pre.ready === 'function') {
          try {
            pre.ready().then(function () {
              self._status.pre = 'ready';
              self._finalize();
            }).catch(function () {
              self._status.pre = preLooksReady ? 'ready' : 'waiting';
              self._finalize();
            });
          } catch (e) {
            self._status.pre = preLooksReady ? 'ready' : (pre ? 'waiting' : 'absent');
            self._finalize();
          }
        } else {
          self._status.pre = preLooksReady ? 'ready' : (pre ? 'waiting' : 'absent');
          self._finalize();
        }

        // No extra finalize here (handled in branches)
      });
    }
  }

  customElements.define(TAG, BoldScAdapter);

  // Auto-insert a single provider if none exists (hidden)
  (function ensureProvider() {
    if (document.querySelector(TAG)) return;
    var el = document.createElement(TAG);
    el.hidden = true;
    if (document.body) document.body.appendChild(el);
    else document.addEventListener('DOMContentLoaded', function () { document.body.appendChild(el); });
  })();

  // Optional: re-probe on editor section loads
  document.addEventListener('shopify:section:load', function () {
    var el = document.querySelector(TAG);
    if (el && el.getAttribute('state') !== 'ready') { el._detect && el._detect(); }
  });
})();
